import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Button, Typography, Box, Container } from "@mui/material";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/RequestStatus";

export default function ProjectLayout() {
  const navigate = useNavigate();

  // 希望条件が入力済み
  const [contractExists, setContractExists] = useState(null);

  // 公開状態
  const [isActive, setIsActive] = useState(false);

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);

  // 初回ロード
  useEffect(() => {
    fetch("/user_contract_request/edit.json")
      .then((res) => res.json())
      .then((data) => {
        setContractExists(data.contract_request_exists);
        setIsActive(data.recruiting_flag);
      })
      .catch((err) => console.error("Error loading request:", err));
  }, []);

  // 公開フラグ更新
  const updateRecruitingFlag = (newStatus) => {
    fetch("/user_contract_request/toggle_recruiting", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({ recruiting_flag: newStatus }),
    }).then((res) => {
      if (res.ok) {
        // 自分の state 更新
        setIsActive(newStatus);

        // ProjectDetailPage に通知
        window.dispatchEvent(
          new CustomEvent("contract:status-updated", {
            detail: { recruiting_flag: newStatus },
          })
        );
      } else {
        alert("公開ステータスの更新に失敗しました。");
      }
    });
  };

  // Switch 切り替え
  const toggleStatus = (event) => {
    const next = event.target.checked;

    // ON → OFF のときだけモーダルを出す
    if (isActive && !next) {
      setModalOpen(true);
      return;
    }

    // ON の場合は即反映
    updateRecruitingFlag(true);
  };

  // モーダル：OFF を確定
  const handleConfirmStop = () => {
    updateRecruitingFlag(false);
    setModalOpen(false);
  };

  // モーダル：キャンセル
  const handleCancelStop = () => {
    setModalOpen(false);
  };

  // 遷移先
  const handleClick = () => {
    navigate(
      `/user_contract_request/edit?redirect=${window.location.pathname}`
    );
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        position: "relative",
        mt: 2,
      }}
    >
      {/* --- 上段：タイトル + 希望条件ボタン --- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: 1,
          mb: 2,
        }}
      >
        {/* 左：タイトル */}
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            fontSize: "clamp(0.7rem, 4vw, 1.8rem)",
            alignItems: "left",
            lineHeight: 1.5,
            whiteSpace: "nowrap",
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          プロジェクト
        </Typography>

        {/* 右：希望条件ボタン */}
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClick}
          sx={{
            position: "relative",
            borderRadius: "6px",
            py: { xs: "6px", sm: "10px" },
            px: { xs: "14px", sm: "22px" },
            fontWeight: 600,
            fontSize: { xs: "0.78rem", sm: "0.95rem" },
            textTransform: "none",
            "&:hover": { opacity: 0.9 },
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {/* 未入力バッジ */}
          {contractExists === false && (
            <Box
              sx={{
                position: "absolute",
                top: "-10px",
                right: "-6px",
                bgcolor: "#f3b6ea",
                color: "secondary",
                fontSize: { xs: "0.55rem", sm: "0.65rem" },
                px: { xs: 0.6, sm: 1 },
                py: "1px",
                borderRadius: "4px",
                fontWeight: "bold",
                boxShadow: 1,
              }}
            >
              未入力あり
            </Box>
          )}
          希望条件を{contractExists ? "編集" : "入力"} +
        </Button>
      </Box>

      {/* --- トグル（希望条件がある場合だけ） --- */}
      {contractExists && (
        <RequestStatus
          viewerType="user"
          type="project"
          isActive={isActive}
          onToggle={toggleStatus}
        />
      )}

      {/* --- 説明文 --- */}
      <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
        {contractExists === false && (
          <>希望条件を入力すると詳細を全て閲覧できます。</>
        )}
        {contractExists && !isActive && (
          <>
            希望条件とあなたのスキルシートはスキルログ運営局に非公開です。
            <br />
            プロジェクトの閲覧はできますが、運営局からの連絡は届きません。
            <br />
            運営局に相談したい場合や紹介を希望する場合は「公開」に設定してください。
          </>
        )}
        {contractExists && isActive && (
          <>
            希望条件とあなたのスキルシートはスキルログ運営局に公開中です。
            <br />
            「詳細を聞きたい」ボタンから運営局に直接コンタクトできます。
            <br />
            条件に合うプロジェクトの紹介もあわせて受け取れます。
          </>
        )}
      </Typography>

      <Outlet context={{ contractExists, isActive }} />

      {/* --- モーダル --- */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title="スキルログ運営局に非公開"
        message="非公開にすると運営局とのコンタクトができなくなります。"
        confirmLabel="非公開にする"
      />
    </Container>
  );
}
